import * as React from "react";
import { cls } from "@utdanningsdirektoratet/utils/cls";
import { Icon, SmallIcon } from "@utdanningsdirektoratet/icon";
import styles from "./Link.module.less";

type HrefProps =
  | {
      href: string;
      children: React.ReactNode;
    }
  | {
      href?: string;
      children: (
        props: {
          style?: React.CSSProperties;
          className: string;
          href?: string;
          ref?: React.ForwardedRef<any>;
          onClick?: (event: any) => void;
          children: React.ReactNode | string;
        },
        children: React.ReactNode | string
      ) => React.ReactNode;
    };

interface LinkProps extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "children"> {
  text?: React.ReactNode | string;
  iconLeft?: SmallIcon;
  iconRight?: SmallIcon;
  bold?: boolean;
  color?: "darkgrey";
  background?: "slate" | "steelblue" | "peach" | "lightazure" | "eggshell" | "green";
  disabled?: boolean;
  className?: string;
}

const Link = React.forwardRef<HTMLAnchorElement, LinkProps & HrefProps>(
  ({ style, href, text, bold, color, iconLeft, iconRight, className, children, disabled, background, ...other }, ref) => {
    const linkClass = cls(
      {
        [styles.link]: true,
        [styles.linkBold]: bold,
        [styles.linkDisabled]: disabled,
        [styles.linkWithIcon]: iconLeft || iconRight,
        [styles.linkWithBackground]: !!background,
      },
      className
    );

    const css = {
      ...style,
      "--link-background-color": background ? `var(--${background}4)` : "transparent",
      "--link-text-color": color === "darkgrey" ? "var(--darkgrey)" : `var(--${background}7)`,
      "--link-text-color-hover": color === "darkgrey" ? "var(--grey)" : `var(--${background}7)`,
    } as React.CSSProperties;

    const iconClass = (placement: "left" | "right") =>
      cls({
        [styles.linkIcon]: true,
        [styles.linkIconLeft]: placement === "left",
        [styles.linkIconRight]: placement === "right",
      });

    const content = (
      <>
        {iconLeft && <Icon icon={iconLeft} className={iconClass("left")} placement="before" />}
        <span className={styles.linkText}>{typeof children === "function" ? text : text || children}</span>
        {iconRight && <Icon icon={iconRight} className={iconClass("right")} placement="after" />}
      </>
    );

    if (typeof children === "function") {
      return (
        <>
          {children(
            {
              style: color || background ? css : style,
              className: linkClass,
              href,
              children: content,
              ref,
              onClick: disabled ? (event) => event.preventDefault() : undefined,
              ...other,
            },
            content
          )}
        </>
      );
    }

    return (
      <a
        style={color || background ? css : style}
        aria-disabled={disabled}
        ref={ref}
        href={href}
        className={linkClass}
        onClick={disabled ? (event) => event.preventDefault() : undefined}
        {...other}
      >
        {content}
      </a>
    );
  }
);

export default Link;
